/*
 * Activity's keyboard bindings, registered into the one shortcut registry (A31) under the scope the
 * page pushes while it is open — which is what makes them appear in the keyboard sheet: an
 * undocumented shortcut cannot exist.
 *
 * A binding is a definition, not a behaviour: the registry is built once per boot, while what a key
 * does depends on the row that has focus. So a definition calls the *controller* the open page
 * installs for its lifetime; with no page open the scope is not on the stack either, and a key that
 * reached here anyway does nothing.
 *
 * `Enter` is deliberately not a binding. The registry's listener sits on the document and prevents
 * the default of everything it answers, which on the *Mark all as read* button would kill the
 * button; a row is a real link and Enter is the link's own key. The label of the first entry
 * documents it, the way the chat module documents `t` and Enter in one label.
 */

import type { ShortcutDef } from "../modules/registry.ts"

/** What the open feed lends its shortcuts. */
export interface ActivityController {
	/** Moves the roving focus by one row; with nothing focused it lands on the first. */
	move(delta: number): void
	/** Marks the row that has focus read, or unread again if it was read. */
	toggleRead(): void
}

let controller: ActivityController | null = null

/** Installed by `ActivityPage` while it is mounted; cleared when it is not. */
export function setActivityController(next: ActivityController | null): void {
	controller = next
}

export const activityShortcuts: ShortcutDef[] = [
	{
		id: "activity.next",
		keys: ["j"],
		scope: "activity",
		labelKey: "activity.shortcut.next",
		run: () => controller?.move(1),
	},
	{
		id: "activity.previous",
		keys: ["k"],
		scope: "activity",
		labelKey: "activity.shortcut.previous",
		run: () => controller?.move(-1),
	},
	{
		id: "activity.toggleRead",
		keys: ["r"],
		scope: "activity",
		labelKey: "activity.shortcut.toggleRead",
		run: () => controller?.toggleRead(),
	},
]
